// src/pages/SignUp.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import cheflylandingpage from "../assets/jagodslandingpage.png";
import { useAuth } from "../Hooks/useAuth";
import { USER_ROLE, API_BASE_URL } from "../helpers/Constant";
import { getDeviceIdSync } from "../Utils/deviceId";
import Toast from "../components/Toast";

const SignUp = () => {
  const navigate = useNavigate();
  const { register, loading, error, clearAuthError } = useAuth();
  const deviceId = getDeviceIdSync();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    password_confirmation: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [tempToken, setTempToken] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [registrationData, setRegistrationData] = useState(null);
  const [isResending, setIsResending] = useState(false);
  const [toast, setToast] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Toast helper
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const hideToast = () => {
    setToast(null);
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = "Name is required";
    } else if (formData.name.trim().length < 2) {
      errors.name = "Name must be at least 2 characters";
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Invalid email address";
    }

    if (!formData.phone.trim()) {
      errors.phone = "Phone number is required";
    } else if (!/^\d{10}$/.test(formData.phone.replace(/[\s-]/g, ""))) {
      errors.phone = "Enter a valid 10-digit phone number";
    }

    if (!formData.password) {
      errors.password = "Password is required";
    } else if (formData.password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }

    if (!formData.password_confirmation) {
      errors.password_confirmation = "Please confirm your password";
    } else if (formData.password !== formData.password_confirmation) {
      errors.password_confirmation = "Passwords do not match";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: "",
      });
    }

    if (error) {
      clearAuthError();
    }
  };

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 6) {
      setOtp(value);
      if (otpError) setOtpError("");
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      setOtpError("Please enter a valid 6-digit OTP");
      return;
    }

    setIsVerifying(true);
    setOtpError("");

    try {
      const response = await fetch(`${API_BASE_URL}/register/verify-otp`, {
        method: 'POST',
        headers: {
          'X-Device-ID': deviceId,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          temp_token: tempToken,
          otp: otp
        })
      });

      const data = await response.json();

      if (data.status_code === 200) {
        setShowOTPModal(false);

        // Store user data (including token) in localStorage
        if (data.data) {
          localStorage.setItem("user", JSON.stringify(data.data));
          // Optionally store token separately if needed
          if (data.data.token) {
            localStorage.setItem("token", data.data.token);
          }
        }

        // Show success message and navigate to home
        showToast("Registration Successful!", 'success');
        setTimeout(() => {
          navigate("/");
        }, 1500);
      } else {
        // Handle errors
        if (data.message && data.message.includes("expired")) {
          setOtpError("Registration session expired. Please sign up again.");
          setTimeout(() => {
            setShowOTPModal(false);
            setOtp("");
            setTempToken("");
          }, 3000);
        } else {
          setOtpError(data.message || "Invalid OTP. Please try again.");
        }
      }
    } catch (error) {
      console.error("OTP Verification Error:", error);
      setOtpError("Network error. Please try again.");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      const registerData = {
        ...formData,
        role: USER_ROLE.CUSTOMER,
      };

      const response = await fetch(`${API_BASE_URL}/reg`, {
        method: 'POST',
        headers: {
          'X-Device-ID': deviceId,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registerData)
      });

      const data = await response.json();

      // Handle 422 validation errors
      if (response.status === 422) {
        if (data.message && typeof data.message === 'object') {
          const errors = {};
          Object.keys(data.message).forEach((key) => {
            const errorMessage = Array.isArray(data.message[key])
              ? data.message[key][0]
              : data.message[key];

            const fieldMap = {
              'email': 'email',
              'phone': 'phone',
              'name': 'name',
              'password': 'password',
              'password_confirmation': 'password_confirmation',
            };

            const frontendField = fieldMap[key] || key;
            errors[frontendField] = errorMessage;
          });

          setFormErrors(errors);
          const firstError = Object.values(errors)[0];
          if (firstError) {
            showToast(firstError, 'error');
          }
        } else if (data.message && typeof data.message === 'string') {
          showToast(data.message, 'error');
        } else {
          showToast('Validation failed. Please check your inputs.', 'error');
        }
        return;
      }

      // Check for success using status_code
      if (data.status_code === 200) {
        setRegistrationData(registerData);
        const token = data.data?.temp_token || data.temp_token;
        if (token) {
          setTempToken(token);
          setShowOTPModal(true);
          setOtp("");
          setOtpError("");
          showToast("OTP sent to your email and phone!", 'success');
        } else {
          showToast("Failed to get verification token. Please try again.", 'error');
        }
      } else {
        // Handle other error statuses
        showToast(data.message || "Registration failed. Please try again.", 'error');
      }
    } catch (error) {
      console.error("REGISTER ERROR => ", error);
      showToast("Network error. Please try again.", 'error');
    }
  };

  const handleResendOTP = async () => {
    if (!tempToken) {
      setOtpError("Session expired. Please sign up again.");
      return;
    }

    setIsResending(true);
    setOtpError("");

    try {
      const response = await fetch(`${API_BASE_URL}/reg`, {
        method: 'POST',
        headers: {
          'X-Device-ID': deviceId,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registrationData)
      });

      const data = await response.json();

      if (response.status === 422) {
        setOtpError(data.message?.email?.[0] || "Invalid registration data.");
        return;
      }

      if (data.status_code === 200) {
        const token = data.data?.temp_token || data.temp_token;
        if (token) {
          setTempToken(token);
          showToast("OTP has been resent to your email/phone", 'success');
          setOtp("");
        } else {
          setOtpError("Failed to resend OTP. Please try again.");
        }
      } else {
        setOtpError(data.message || "Failed to resend OTP. Please try again.");
      }
    } catch (error) {
      console.error("Resend OTP Error:", error);
      setOtpError("Failed to resend OTP. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  // OTP Modal Component
  const OTPModal = () => {
    if (!showOTPModal) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl">
          <div className="text-center">
            <div className="mb-4 flex justify-center">
              <div className="rounded-full bg-purple-100 p-3">
                <svg className="h-8 w-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-900">Verify Your Account</h3>
            <p className="mt-2 text-sm text-gray-600">
              We've sent a verification code to your email and phone. Please enter the 6-digit OTP below.
            </p>
            {tempToken && (
              <p className="mt-1 text-xs text-gray-500">
                Token: {tempToken.substring(0, 8)}...
              </p>
            )}
          </div>

          <div className="mt-6">
            <label className="mb-2 block text-sm font-semibold text-gray-700">
              Enter OTP
            </label>
            <div className="flex h-[60px] items-center overflow-hidden rounded-2xl border border-gray-200 bg-gray-50 focus-within:border-purple-500 focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(108,51,205,0.12)]">
              <input
                type="text"
                inputMode="numeric"
                maxLength="6"
                value={otp}
                onChange={handleOtpChange}
                placeholder="Enter 6-digit code"
                className="h-full w-full bg-transparent px-5 text-center text-lg font-semibold tracking-widest outline-none placeholder:text-gray-400"
                autoFocus
              />
            </div>
            {otpError && (
              <p className="mt-2 text-sm text-red-500">{otpError}</p>
            )}
          </div>

          <div className="mt-6 flex flex-col gap-3">
            <button
              onClick={handleVerifyOTP}
              disabled={isVerifying}
              className="h-[52px] w-full rounded-2xl bg-purple-600 text-base font-bold text-white shadow-[0_10px_30px_rgba(108,51,205,0.3)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {isVerifying ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="h-5 w-5 animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Verifying...
                </span>
              ) : (
                "Verify OTP"
              )}
            </button>

            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={handleResendOTP}
                disabled={isResending}
                className="text-purple-600 hover:underline font-medium disabled:opacity-50"
              >
                {isResending ? "Sending..." : "Resend OTP"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowOTPModal(false);
                  setOtp("");
                  setOtpError("");
                }}
                className="text-gray-500 hover:text-gray-700 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <section className="min-h-screen overflow-hidden bg-[#fff7f2]">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}

      <div className="mx-auto grid min-h-screen max-w-[1800px] grid-cols-1 lg:grid-cols-2">
        {/* Left Side - Form */}
        <div className="flex min-h-screen items-center justify-center bg-white px-6 py-8 sm:px-10 lg:px-14">
          <div className="w-full max-w-[520px]">
            <h2 className="text-4xl font-black leading-tight text-[#111827] sm:text-5xl">
              Create Account
            </h2>

            <p className="mt-3 max-w-md text-base leading-relaxed text-[#6b7280]">
              Sign up to explore restaurants, exclusive offers and lightning-fast delivery.
            </p>

            {error && (
              <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 p-4">
                <p className="text-sm font-medium text-red-500">{error}</p>
                <button
                  onClick={clearAuthError}
                  className="mt-2 text-xs font-medium text-red-400 hover:text-red-600"
                >
                  Dismiss
                </button>
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-8 space-y-4">
              {/* Full Name */}
              <div>
                <label className="mb-2 block text-sm font-semibold text-[#374151]">
                  Full Name
                </label>
                <div
                  className={`group flex h-[58px] items-center overflow-hidden rounded-2xl border bg-[#f9fafb] transition-all duration-300 focus-within:border-[#6c33cd] focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(108,51,205,0.12)] ${
                    formErrors.name ? "border-red-500" : "border-[#e5e7eb]"
                  }`}
                >
                  <input
                    type="text"
                    name="name"
                    placeholder="Enter your full name"
                    value={formData.name}
                    onChange={handleChange}
                    className="h-full w-full bg-transparent px-5 text-[15px] outline-none placeholder:text-[#9ca3af]"
                  />
                </div>
                {formErrors.name && (
                  <p className="mt-1 text-sm text-red-500">{formErrors.name}</p>
                )}
              </div>

              {/* Email + Phone Row */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-semibold text-[#374151]">
                    Email Address
                  </label>
                  <div
                    className={`group flex h-[58px] items-center overflow-hidden rounded-2xl border bg-[#f9fafb] transition-all duration-300 focus-within:border-[#6c33cd] focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(108,51,205,0.12)] ${
                      formErrors.email ? "border-red-500" : "border-[#e5e7eb]"
                    }`}
                  >
                    <input
                      type="email"
                      name="email"
                      placeholder="Enter email"
                      value={formData.email}
                      onChange={handleChange}
                      className="h-full w-full bg-transparent px-5 text-[15px] outline-none placeholder:text-[#9ca3af]"
                    />
                  </div>
                  {formErrors.email && (
                    <p className="mt-1 text-sm text-red-500">{formErrors.email}</p>
                  )}
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-[#374151]">
                    Mobile Number
                  </label>
                  <div
                    className={`group flex h-[58px] items-center overflow-hidden rounded-2xl border bg-[#f9fafb] transition-all duration-300 focus-within:border-[#6c33cd] focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(108,51,205,0.12)] ${
                      formErrors.phone ? "border-red-500" : "border-[#e5e7eb]"
                    }`}
                  >
                    <input
                      type="tel"
                      name="phone"
                      placeholder="Enter mobile number"
                      value={formData.phone}
                      onChange={handleChange}
                      className="h-full w-full bg-transparent px-5 text-[15px] outline-none placeholder:text-[#9ca3af]"
                    />
                  </div>
                  {formErrors.phone && (
                    <p className="mt-1 text-sm text-red-500">{formErrors.phone}</p>
                  )}
                </div>
              </div>

              {/* Password Row with Eye Icons */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-semibold text-[#374151]">
                    Password
                  </label>
                  <div
                    className={`group flex h-[58px] items-center overflow-hidden rounded-2xl border bg-[#f9fafb] transition-all duration-300 focus-within:border-[#6c33cd] focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(108,51,205,0.12)] ${
                      formErrors.password ? "border-red-500" : "border-[#e5e7eb]"
                    }`}
                  >
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="Enter password"
                      value={formData.password}
                      onChange={handleChange}
                      className="h-full w-full bg-transparent px-5 text-[15px] outline-none placeholder:text-[#9ca3af]"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="pr-5 text-[#9ca3af] hover:text-[#6c33cd] transition-colors"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {formErrors.password && (
                    <p className="mt-1 text-sm text-red-500">{formErrors.password}</p>
                  )}
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-[#374151]">
                    Confirm Password
                  </label>
                  <div
                    className={`group flex h-[58px] items-center overflow-hidden rounded-2xl border bg-[#f9fafb] transition-all duration-300 focus-within:border-[#6c33cd] focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(108,51,205,0.12)] ${
                      formErrors.password_confirmation ? "border-red-500" : "border-[#e5e7eb]"
                    }`}
                  >
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      name="password_confirmation"
                      placeholder="Confirm password"
                      value={formData.password_confirmation}
                      onChange={handleChange}
                      className="h-full w-full bg-transparent px-5 text-[15px] outline-none placeholder:text-[#9ca3af]"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="pr-5 text-[#9ca3af] hover:text-[#6c33cd] transition-colors"
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    >
                      {showConfirmPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {formErrors.password_confirmation && (
                    <p className="mt-1 text-sm text-red-500">{formErrors.password_confirmation}</p>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-3 pt-1">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded accent-[#6c33cd]"
                  required
                />
                <p className="text-sm leading-relaxed text-[#6b7280]">
                  By creating an account, you agree to our{" "}
                  <span className="font-semibold text-[#6c33cd] cursor-pointer hover:underline">
                    Terms
                  </span>{" "}
                  and{" "}
                  <span className="font-semibold text-[#6c33cd] cursor-pointer hover:underline">
                    Privacy Policy
                  </span>
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="mt-2 h-[60px] w-full rounded-2xl bg-[#6c33cd] text-base font-bold text-white shadow-[0_15px_40px_rgba(108,51,205,0.35)] transition-all duration-300 hover:-translate-y-1 hover:bg-[#5b21b6] disabled:cursor-not-allowed disabled:opacity-70"
              >
                {loading ? "Creating Account..." : "Create Account"}
              </button>

              <p className="pt-2 text-center text-sm text-[#6b7280]">
                Already have an account?{" "}
                <span
                  onClick={() => navigate("/signin")}
                  className="cursor-pointer font-bold text-[#6c33cd] hover:underline"
                >
                  Login
                </span>
              </p>
            </form>
          </div>
        </div>

        {/* Right Side - Image */}
        <div className="hidden lg:flex relative h-screen overflow-hidden bg-[#fff7f2]">
          <img
            src={cheflylandingpage}
            alt="Food Delivery"
            className="w-full h-full object-cover"
          />
        </div>
      </div>

      {/* OTP Modal */}
      <OTPModal />
    </section>
  );
};

export default SignUp;